/**
 * Values and catalogues (ADR-119 decision 2). A value is data, never a function: a string with named placeholders,
 * or a plural object keyed by the categories of `Intl.PluralRules` and chosen by the `count` parameter. A catalogue
 * is a language's tree of values beside the locale its numbers, lists, dates and plurals are formatted in, so the
 * bundled English, a translation and a published wording file are each one catalogue and no caller changes.
 */

import { templateNames } from "./placeholders.ts";

/**
 * The plural categories `Intl.PluralRules` selects, in its own order.
 */
export const PLURAL_CATEGORIES = Object.freeze(["zero", "one", "two", "few", "many", "other"] as const);

/**
 * One plural category.
 */
export type PluralCategory = (typeof PLURAL_CATEGORIES)[number];

/**
 * A plural object: one text per category. `other` is the form every language has; a value without it is not used.
 */
export type PluralValue = { readonly [category in PluralCategory]?: string };

/**
 * A value: a string with named placeholders, or a plural object.
 */
export type WordValue = string | PluralValue;

/**
 * A tree of values, keyed by the segments of a catalogue key or a derived key. An object whose keys are all plural
 * categories and whose members are all strings is a plural value, never a branch.
 */
export interface WordTree {
  readonly [segment: string]: WordValue | WordTree;
}

/**
 * A catalogue: a BCP 47 locale (`en`, `de`, `pt-BR`) and its words.
 */
export interface Catalogue {
  readonly locale: string;
  readonly words: WordTree;
}

/**
 * The plural categories as a set, for the shape check.
 */
const CATEGORY_SET: ReadonlySet<string> = new Set<string>(PLURAL_CATEGORIES);

/**
 * Whether a value is a plain object: not null, not an array.
 *
 * @param value Anything.
 * @returns True for a plain object.
 */
function isRecord(value: unknown): value is { readonly [key: string]: unknown } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether a value is a plural object: at least one member, every key a plural category and every member a string.
 * `other` is not required here, so a check can name a plural that lacks it.
 *
 * @param value Anything.
 * @returns True for a plural object.
 */
export function isPluralValue(value: unknown): value is PluralValue {
  if (!isRecord(value)) {
    return false;
  }
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => CATEGORY_SET.has(key) && typeof value[key] === "string");
}

/**
 * Whether a value is a word value: a string or a plural object.
 *
 * @param value Anything.
 * @returns True for a string or a plural object.
 */
export function isWordValue(value: unknown): value is WordValue {
  return typeof value === "string" || isPluralValue(value);
}

/**
 * The value at a key's dotted path, reading only the tree's own members, so a key from the wire can never reach an
 * object's prototype.
 *
 * @param tree The tree of values.
 * @param key The key.
 * @returns The string or plural object at the path, or undefined when the path is missing, ends at a branch or runs
 * through a value.
 */
export function wordAt(tree: WordTree, key: string): WordValue | undefined {
  let node: unknown = tree;
  for (const segment of key.split(".")) {
    if (!isRecord(node) || isPluralValue(node) || !Object.hasOwn(node, segment)) {
      return undefined;
    }
    node = node[segment];
  }
  return isWordValue(node) ? node : undefined;
}

/**
 * The plural object's texts, in category order.
 *
 * @param value The plural object.
 * @returns Each form it holds.
 */
function pluralForms(value: PluralValue): readonly string[] {
  const forms: string[] = [];
  for (const category of PLURAL_CATEGORIES) {
    const form = value[category];
    if (form !== undefined) {
      forms.push(form);
    }
  }
  return forms;
}

/**
 * The parameter names a value takes, sorted: its placeholders, and `count` for a plural object.
 *
 * @param value The value.
 * @returns The names, or null when any of its texts does not parse.
 */
export function valueNames(value: WordValue): readonly string[] | null {
  const texts = typeof value === "string" ? [value] : pluralForms(value);
  const names = new Set<string>(typeof value === "string" ? [] : ["count"]);
  for (const text of texts) {
    const found = templateNames(text);
    if (found === null) {
      return null;
    }
    for (const name of found) {
      names.add(name);
    }
  }
  return [...names].sort();
}

/**
 * Whether a catalogue's value may stand in for the fallback's: both parse, both are strings or both plural objects,
 * a plural object holds `other`, and the two take the same parameter names. A value that does not fit is never
 * shown; the fallback's is.
 *
 * @param value The catalogue's value.
 * @param fallback The fallback's value for the same key.
 * @returns True when the value fits.
 */
export function fitsFallback(value: WordValue, fallback: WordValue): boolean {
  if (typeof value !== typeof fallback || !isUsable(value) || !isUsable(fallback)) {
    return false;
  }
  const own = valueNames(value);
  const wanted = valueNames(fallback);
  return own !== null && wanted !== null && own.length === wanted.length && own.every((name, index) => name === wanted[index]);
}

/**
 * Whether a value can be shown at all: every text parses, and a plural object holds `other`.
 *
 * @param value The value.
 * @returns True when the value can be shown.
 */
export function isUsable(value: WordValue): boolean {
  if (typeof value !== "string" && value.other === undefined) {
    return false;
  }
  return valueNames(value) !== null;
}
